/**
 * Cloud Operations — Deploy paved-road landing.
 *
 * Replaces the original entity-list landing. Pattern (per user feedback
 * 2026-06-10 + memory feedback_cloud_ops_paved_road):
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  Cloud Operations          Manage catalog →                │
 *   │  Deploy Prove7 to your preferred cloud or self-host        │
 *   │  ┌──────────┬──────────┬──────────┬──────────────────────┐ │
 *   │  │Configure │Networking│Observabil│Existing Deployments  │ │
 *   │  └──────────┴──────────┴──────────┴──────────────────────┘ │
 *   │  ┌─────────────────────┐  ┌─────────────────────────────┐  │
 *   │  │ Prove7 Cloud  ✓     │  │ Self-Hosted                 │  │
 *   │  │ Fully managed       │  │ K8s/Docker · own infra      │  │
 *   │  └─────────────────────┘  └─────────────────────────────┘  │
 *   │  ┌── tab content ─────────────────────────────────────────┐│
 *   │  │  (Configure / Networking / Observability / Existing)   ││
 *   │  └────────────────────────────────────────────────────────┘│
 *   └────────────────────────────────────────────────────────────┘
 *
 * Mode card selection drives the Configure form variant. Self-Hosted exposes
 * the WIF / deploy SA inputs inline; Prove7 Cloud hides credentials entirely
 * (Prove7 owns the underlying GCP — customer contract drives region + size).
 *
 * Backend label stays `prove7_hosted` for column stability; display uses
 * modeLabel() helper. See memory: reference_prove7_cloud_naming.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPlatformVersions, getDeployPreflight, listOrderFormDeployments } from '../../services/cloudOperationsApi';
import * as api from '../../services/api';
import { T, btn } from './_ui.jsx';
import DeployConfigureTab from './DeployConfigureTab.jsx';
import DeployNetworkingTab from './DeployNetworkingTab.jsx';
import DeployObservabilityTab from './DeployObservabilityTab.jsx';
import DeployExistingTab from './DeployExistingTab.jsx';

const TABS = [
  { id: 'configure',     label: 'Configure' },
  { id: 'networking',    label: 'Networking' },
  { id: 'observability', label: 'Observability' },
  { id: 'existing',      label: 'Existing Deployments' },
];

const DEFAULT_FORM = {
  mode: 'prove7_hosted',               // wire value; UI displays "Prove7 Cloud"
  // Contract-driven deploy: the customer whose signed order form we are fulfilling,
  // and the specific entitled deployment line. Both empty until the operator picks
  // them; the Deploy CTA stays disabled until a line is selected. When set, the
  // backend fulfils the order line and takes ALL contracted infra from it — the
  // free-form infra inputs below become a read-only confirmation of the contract.
  customer_id: '',
  order_line_id: '',
  deployment_name: '',
  provider: 'gcp',
  primary_region: 'us-central1',
  paired_region: '',
  sla_tier: 'single_region',
  instance_size: 'M',
  // Resolved from the server on mount (PlatformVersionCatalog). Left empty
  // so a stale client-side pin can never be submitted; the Deploy CTA's
  // required-field guard covers the pre-load moment.
  platform_version: '',
  // Self-Hosted only — empty in Prove7 Cloud mode.
  cloud_account: {
    name: '',
    gcp_project_id: '',
    wif_pool_id: '',
    wif_provider_id: '',
    deploy_service_account_email: '',
  },
  // Resilience toggles live on the configure form; default mirrors most-used
  // production posture per the runbook.
  resilience: {
    multi_az: true,
    auto_scaling: true,
    tls_termination: true,
    private_link: false,
  },
  // Networking + Observability tabs hold their own state but share the same
  // top-level form so a single Deploy click submits everything.
  networking: {
    ingress_controller: 'nginx',
    custom_domain: '',
    tls_cert_arn: '',
    cors: true,
    rate_limiting: true,
    mtls_mode: 'strict',
    service_cidr: '10.100.0.0/16',
    pod_cidr:     '10.200.0.0/16',
    network_policies: true,
    egress_filtering: false,
  },
  observability: {
    metrics_backend: 'prometheus',
    metrics_endpoint: '/metrics',
    tracing_backend: 'jaeger',
    otlp_endpoint:   'http://otel-collector:4317',
    distributed_tracing: true,
    log_aggregator:  'elasticsearch',
    log_level:       'info',
    loki_es_endpoint: 'http://loki:3100',
    structured_json_logging: true,
    audit_log_forwarding: true,
  },
};

export default function DeployHomePage() {
  const { user } = useAuth() || {};
  const role = user?.role || user?.currentRole;

  const [tab, setTab] = useState('configure');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [platformVersions, setPlatformVersions] = useState([]);
  const [preflight, setPreflight] = useState(null);

  // Contract-driven deploy: the customers this operator may deploy for, and the
  // entitled / already-provisioned deployment lines for the selected customer.
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [entitled, setEntitled] = useState([]);
  const [provisioned, setProvisioned] = useState([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);

  // Deployable versions come from the server, never a hard-coded list.
  // Adopt the server's default only while the operator hasn't chosen one, so
  // a late response can't clobber an in-progress selection.
  useEffect(() => {
    let cancelled = false;
    getPlatformVersions()
      .then((res) => {
        if (cancelled || !res) return;
        const list = Array.isArray(res.versions) ? res.versions : [];
        setPlatformVersions(list);
        const fallback = res.default || (list[0] && list[0].version) || '';
        if (fallback) setForm((f) => (f.platform_version ? f : { ...f, platform_version: fallback }));
      })
      .catch(() => { /* leave empty — the CTA's required-field guard blocks submit */ });
    return () => { cancelled = true; };
  }, []);

  // Load the customers this operator may deploy for. Same unwrap shape as every
  // other cross-customer surface (api.listCustomers resolves the apiFetch envelope).
  useEffect(() => {
    let cancelled = false;
    setCustomersLoading(true);
    api.listCustomers()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setCustomers(rows);
      })
      .catch(() => { if (!cancelled) setCustomers([]); })
      .finally(() => { if (!cancelled) setCustomersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // When the selected customer changes, (re)load their order-form deployments.
  // No customer → nothing entitled; the picker invites one to be chosen.
  const customerId = form.customer_id;
  useEffect(() => {
    if (!customerId) { setEntitled([]); setProvisioned([]); return undefined; }
    let cancelled = false;
    setDeploymentsLoading(true);
    listOrderFormDeployments(customerId)
      .then((res) => {
        if (cancelled) return;
        setEntitled(Array.isArray(res?.entitled) ? res.entitled : []);
        setProvisioned(Array.isArray(res?.provisioned) ? res.provisioned : []);
      })
      .catch(() => { if (!cancelled) { setEntitled([]); setProvisioned([]); } })
      .finally(() => { if (!cancelled) setDeploymentsLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  // The entitled line currently selected. Its envelope carries the contracted
  // infra (mode, provider, region, size, SLA) — the single source of truth once
  // a line is chosen, so preflight and the CTA read from it, not the free-form form.
  const selectedEnvelope = entitled.find((e) => e.lineId === form.order_line_id) || null;

  // Re-check readiness whenever the mode/provider the operator is deploying to
  // changes — a preflight for a different target would be misleading. In
  // contract-driven mode the effective target is the entitled line's envelope.
  const formMode = selectedEnvelope ? selectedEnvelope.deploymentMode : form.mode;
  const formProvider = selectedEnvelope ? selectedEnvelope.provider : form.provider;
  useEffect(() => {
    let cancelled = false;
    getDeployPreflight({ mode: formMode, provider: formProvider })
      .then((res) => { if (!cancelled) setPreflight(res || null); })
      .catch(() => { if (!cancelled) setPreflight(null); });
    return () => { cancelled = true; };
  }, [formMode, formProvider]);

  // Shallow setters so each tab edits a slice without overwriting siblings.
  const setMode      = (mode) => setForm((f) => ({ ...f, mode }));
  const updateRoot   = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateCloud  = (patch) => setForm((f) => ({ ...f, cloud_account: { ...f.cloud_account, ...patch } }));
  const updateResil  = (patch) => setForm((f) => ({ ...f, resilience: { ...f.resilience, ...patch } }));
  const updateNet    = (patch) => setForm((f) => ({ ...f, networking: { ...f.networking, ...patch } }));
  const updateObs    = (patch) => setForm((f) => ({ ...f, observability: { ...f.observability, ...patch } }));

  // EntityPicker/CustomerPicker emit the selected item's id (or null), not the item.
  // Changing the customer always clears the previously-selected line — a line
  // from customer A is never valid for customer B (the backend rejects it, but
  // the UI should never offer it either).
  const selectCustomer = (customerIdSel) => setForm((f) => ({ ...f, customer_id: customerIdSel || '', order_line_id: '' }));
  const selectLine     = (lineId) => setForm((f) => ({ ...f, order_line_id: lineId || '' }));

  return (
    <div style={{
      padding: '20px 32px 32px',
      maxWidth: 1400,
      margin: '0 auto',
      color: T.text,
    }}>
      <Header role={role} />
      <TabStrip current={tab} onChange={setTab} />

      {/* Mode cards always render — they're the persistent choice that drives
          every other tab. Hiding them under "Configure" would make
          Networking / Observability look like they apply to "whatever mode
          you happen to be on now", which is confusing. */}
      {tab !== 'existing' && !form.customer_id && (
        <ModeCards mode={form.mode} onChange={setMode} />
      )}

      <div style={{ minHeight: 360 }}>
        {tab === 'configure' && (
          <DeployConfigureTab
            form={form}
            updateRoot={updateRoot}
            updateCloud={updateCloud}
            updateResil={updateResil}
            platformVersions={platformVersions}
            preflight={preflight}
            customers={customers}
            customersLoading={customersLoading}
            entitled={entitled}
            provisioned={provisioned}
            deploymentsLoading={deploymentsLoading}
            selectedEnvelope={selectedEnvelope}
            onSelectCustomer={selectCustomer}
            onSelectLine={selectLine}
          />
        )}
        {tab === 'networking' && (
          <DeployNetworkingTab form={form} updateNet={updateNet} />
        )}
        {tab === 'observability' && (
          <DeployObservabilityTab form={form} updateObs={updateObs} />
        )}
        {tab === 'existing' && (
          <DeployExistingTab />
        )}
      </div>
    </div>
  );
}

function Header({ role }) {
  const scopeLine = role === 'Platform Admin'
    ? 'Platform · cross-customer view'
    : role === 'Super Admin'
      ? 'Customer-scoped view'
      : 'Platform · PA + Super Admin';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
      <div>
        <div style={{ fontSize: 12, color: T.text3, letterSpacing: '.04em', marginBottom: 2 }}>
          {scopeLine}
        </div>
        <h1 style={{ margin: 0, fontSize: 26, color: T.ink, fontWeight: 700 }}>
          Cloud Operations
        </h1>
        <div style={{ color: T.text2, fontSize: 13, marginTop: 4 }}>
          Deploy Prove7 to your preferred cloud or self-host on your own infrastructure.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {/* Order Forms authoring is Platform-Admin only — it is what entitles the
            deployments this page provisions. Super Admins deploy but do not author. */}
        {role === 'Platform Admin' && (
          <Link
            to="/cloud-operations/order-forms"
            style={{ ...btn, textDecoration: 'none', fontSize: 12, padding: '6px 12px' }}
          >
            Order Forms →
          </Link>
        )}
        {/* Power-user escape hatch — Cloud Accounts, Targets, SLA Matrix, etc.
            still live behind /cloud-operations/advanced. Direct URLs work too.
            Labeled "Advanced Ops" per user feedback 2026-06-10. */}
        <Link
          to="/cloud-operations/advanced"
          style={{ ...btn, textDecoration: 'none', fontSize: 12, padding: '6px 12px' }}
        >
          Advanced Ops →
        </Link>
      </div>
    </div>
  );
}

function TabStrip({ current, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      gap: 4,
      padding: 4,
      background: T.bgAlt,
      borderRadius: 8,
      marginBottom: 20,
    }}>
      {TABS.map((t) => {
        const active = current === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              border: 'none',
              borderRadius: 6,
              padding: '7px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? T.action : 'transparent',
              color:      active ? '#fff'   : T.text2,
              transition: 'background .12s, color .12s',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ModeCards({ mode, onChange }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 16,
      marginBottom: 24,
    }}>
      <ModeCard
        active={mode === 'prove7_hosted'}
        accent={T.action}
        title="Prove7 Cloud"
        icon="☁"
        tagline="Fully managed · SLA-backed · zero ops overhead"
        onClick={() => onChange('prove7_hosted')}
      />
      <ModeCard
        active={mode === 'self_hosted'}
        accent="#6b3f9e"
        title="Self-Hosted"
        icon="▦"
        tagline="K8s or Docker · full control · bring your own infra"
        onClick={() => onChange('self_hosted')}
      />
    </div>
  );
}

function ModeCard({ active, accent, title, icon, tagline, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: `2px solid ${active ? accent : T.stroke}`,
        borderRadius: 10,
        padding: 20,
        cursor: 'pointer',
        boxShadow: active ? `0 0 0 4px ${accent}14` : 'none',
        transition: 'border-color .12s, box-shadow .12s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 8,
          background: `${accent}14`,
          display: 'grid', placeItems: 'center',
          fontSize: 18, color: accent, fontWeight: 700,
        }}>{icon}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: active ? accent : T.ink }}>
          {title}
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, color: T.text2 }}>
        {tagline}
      </div>
      <div style={{ marginTop: 12 }}>
        {active ? (
          <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            color: accent,
            background: `${accent}14`,
            border: `1px solid ${accent}44`,
          }}>Selected</span>
        ) : (
          <span style={{ fontSize: 12, color: T.text3 }}>Click to select</span>
        )}
      </div>
    </button>
  );
}
